import React from "react";
import { format, parseISO, differenceInDays, isBefore, isToday } from "date-fns";
import { ptBR } from "date-fns/locale";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, AlertTriangle, Clock, CheckCircle2, Calendar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function LembretesPanel({ contas, onMarcarPago, onVerConta }) {
  const hoje = new Date();
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  // Filtrar contas que precisam de atenção
  const contasComLembrete = contas
    .filter(c => c.status !== 'pago')
    .map(c => {
      const vencimento = parseISO(c.data_vencimento);
      const diasParaVencer = differenceInDays(vencimento, hoje);
      const atrasada = isBefore(vencimento, hoje) && !isToday(vencimento);
      const venceHoje = isToday(vencimento);
      const dentroDoLembrete = diasParaVencer <= (c.lembrete_dias || 3) && diasParaVencer >= 0;
      
      return {
        ...c,
        vencimento,
        diasParaVencer,
        atrasada,
        venceHoje,
        dentroDoLembrete,
        prioridade: atrasada ? 0 : venceHoje ? 1 : dentroDoLembrete ? 2 : 3
      };
    })
    .filter(c => c.atrasada || c.venceHoje || c.dentroDoLembrete)
    .sort((a, b) => a.prioridade - b.prioridade || a.diasParaVencer - b.diasParaVencer);

  const atrasadas = contasComLembrete.filter(c => c.atrasada);
  const vencemHoje = contasComLembrete.filter(c => c.venceHoje);
  const proximas = contasComLembrete.filter(c => c.dentroDoLembrete && !c.venceHoje);

  if (contasComLembrete.length === 0) {
    return (
      <Card className="bg-green-50 border-green-200">
        <CardContent className="p-6 text-center">
          <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <h3 className="font-bold text-green-800">Tudo em dia!</h3>
          <p className="text-sm text-green-600 mt-1">Nenhuma conta próxima do vencimento</p>
        </CardContent>
      </Card>
    );
  }

  const renderConta = (conta, tipo) => {
    const bgColor = tipo === 'atrasada' ? 'bg-red-50 border-red-200' :
                    tipo === 'hoje' ? 'bg-amber-50 border-amber-200' :
                    'bg-blue-50 border-blue-200';
    
    const textColor = tipo === 'atrasada' ? 'text-red-700' :
                      tipo === 'hoje' ? 'text-amber-700' :
                      'text-blue-700';

    return (
      <motion.div
        key={conta.id}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className={`p-4 rounded-xl border ${bgColor}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-stone-800 truncate">{conta.descricao}</p>
            <p className="text-sm text-stone-500 mt-1">
              {conta.fornecedor && `${conta.fornecedor} • `}
              {format(conta.vencimento, "dd/MM/yyyy")}
            </p>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={`${bgColor} ${textColor} border`}>
                {tipo === 'atrasada' ? `${Math.abs(conta.diasParaVencer)} dias atrasada` :
                 tipo === 'hoje' ? 'Vence hoje!' :
                 `Vence em ${conta.diasParaVencer} dias`}
              </Badge>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-xl font-bold ${textColor}`}>{formatCurrency(conta.valor)}</p>
            <Button
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={() => onMarcarPago(conta)}
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Pagar
            </Button>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <Card className="bg-white border-stone-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-amber-500" />
          Lembretes e Alertas
          <Badge variant="secondary">{contasComLembrete.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Atrasadas */}
        {atrasadas.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <span className="font-semibold text-red-700">Atrasadas ({atrasadas.length})</span>
            </div>
            <div className="space-y-2">
              <AnimatePresence>
                {atrasadas.slice(0, 3).map(c => renderConta(c, 'atrasada'))}
              </AnimatePresence>
            </div>
          </div>
        )}

        {/* Vencem Hoje */}
        {vencemHoje.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-amber-600" />
              <span className="font-semibold text-amber-700">Vencem Hoje ({vencemHoje.length})</span>
            </div>
            <div className="space-y-2">
              <AnimatePresence>
                {vencemHoje.map(c => renderConta(c, 'hoje'))}
              </AnimatePresence>
            </div>
          </div>
        )}

        {/* Próximas */}
        {proximas.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span className="font-semibold text-blue-700">Próximos Vencimentos ({proximas.length})</span>
            </div>
            <div className="space-y-2">
              <AnimatePresence>
                {proximas.slice(0, 5).map(c => renderConta(c, 'proxima'))}
              </AnimatePresence>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}